/**
 * `ApiRestGuideContent` — contenuto della guida alle **API REST** pubbliche,
 * mostrato come sezione del menù laterale della pagina «Amministrazione»
 * (`/admin/scoring`, sezione «Documentazione API»).
 *
 * Documenta gli endpoint HTTP per leggere le certificazioni, autenticati via
 * header `x-api-key`, in uno stile ispirato a Swagger: badge del metodo, path,
 * parametri, header di autenticazione e modello JSON della risposta. La
 * gestione delle chiavi (creazione/revoca) vive nella sezione «API keys» della
 * stessa pagina. Il layout (Card con titolo di sezione) è allineato alle altre
 * sottopagine admin.
 */
import type { ReactNode } from 'react';
import { Card } from '../components/ui';

/** Metodi HTTP supportati dagli endpoint documentati. */
type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

/** Un parametro (di query o di path) di un endpoint. */
interface EndpointParam {
  name: string;
  /** `query` o `path`. */
  location: 'query' | 'path';
  type: string;
  required: boolean;
  description: ReactNode;
}

/**
 * Blocco "stile Swagger" per un singolo endpoint: riga metodo + path, sommario,
 * parametri, header di autenticazione e modello JSON della risposta.
 */
function EndpointBlock({
  method,
  path,
  summary,
  params,
  responseStatus = '200 OK',
  responseModel,
  example,
}: {
  method: HttpMethod;
  path: string;
  summary: ReactNode;
  params?: EndpointParam[];
  responseStatus?: string;
  responseModel: string;
  example?: string;
}) {
  return (
    <div className="api-endpoint">
      <div className="api-endpoint__head">
        <span
          className={`api-endpoint__method api-endpoint__method--${method.toLowerCase()}`}
        >
          {method}
        </span>
        <code className="api-endpoint__path">{path}</code>
      </div>

      <p className="api-endpoint__summary">{summary}</p>

      <div className="api-endpoint__section">
        <h4 className="api-endpoint__label">Autenticazione</h4>
        <p className="api-docs__desc">
          Header <code>x-api-key: &lt;chiave&gt;</code> (obbligatorio).
        </p>
      </div>

      {params !== undefined && params.length > 0 && (
        <div className="api-endpoint__section">
          <h4 className="api-endpoint__label">Parametri</h4>
          <ul className="api-endpoint__params">
            {params.map((p) => (
              <li key={`${p.location}:${p.name}`} className="api-endpoint__param">
                <code className="api-endpoint__param-name">{p.name}</code>
                <span className="api-endpoint__param-meta">
                  {p.location} · {p.type}
                  {p.required ? ' · obbligatorio' : ' · facoltativo'}
                </span>
                <span className="api-endpoint__param-desc">{p.description}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="api-endpoint__section">
        <h4 className="api-endpoint__label">
          Risposta <span className="api-endpoint__status">{responseStatus}</span>
        </h4>
        <pre className="api-docs__code">
          <code>{responseModel}</code>
        </pre>
      </div>

      {example !== undefined && (
        <div className="api-endpoint__section">
          <h4 className="api-endpoint__label">Esempio di richiesta</h4>
          <pre className="api-docs__code">
            <code>{example}</code>
          </pre>
        </div>
      )}
    </div>
  );
}

/** Modello di risposta di `GET /api/public/certifications/company`. */
const COMPANY_CERT_RESPONSE_MODEL = `// Array di certificazioni aziendali
[
  {
    "id": "string",
    "name": "string",
    "issuer": "string | assente",
    "description": "string | assente",
    "url": "string | assente",
    "imageUrl": "string | assente",
    "issuedAt": "string (ISO-8601) | null",
    "expiresAt": "string (ISO-8601) | null"
  }
]`;

/** Modello di risposta di `GET /api/public/users/certifications`. */
const USER_CERTS_RESPONSE_MODEL = `{
  "email": "string",
  "displayName": "string",
  "certifications": [
    {
      "certificationId": "string",
      "name": "string",
      "issuer": "string | assente",
      "imageUrl": "string | assente",
      "source": "manual | credly | linkedin",
      "issuedAt": "string (ISO-8601) | null",
      "expiresAt": "string (ISO-8601) | null",
      "badgeUrl": "string | assente"
    }
  ]
}`;

export function ApiRestGuideContent() {
  return (
    <div className="admin-section-stack">
      <Card>
        <h2 className="scoring-admin__section-title">API REST</h2>
        <p className="scoring-admin__section-help">
          Endpoint HTTP di sola lettura per le certificazioni, autenticati
          tramite header <code>x-api-key: &lt;chiave&gt;</code>. Crea una chiave
          nella sezione «API keys» e sostituisci <code>&lt;BASE_URL&gt;</code>{' '}
          con il dominio dell&apos;API.
        </p>
      </Card>

      <Card>
        <EndpointBlock
          method="GET"
          path="<BASE_URL>/api/public/certifications/company"
          summary="Elenco di tutte le certificazioni aziendali con nome, ente, descrizione e date di conseguimento/scadenza."
          responseModel={COMPANY_CERT_RESPONSE_MODEL}
          example={'GET <BASE_URL>/api/public/certifications/company\nx-api-key: <chiave>'}
        />
      </Card>

      <Card>
        <EndpointBlock
          method="GET"
          path="<BASE_URL>/api/public/users/certifications"
          summary="Certificazioni possedute dal dipendente identificato dall'email. Risponde 404 se nessun dipendente ha quell'email."
          params={[
            {
              name: 'email',
              location: 'query',
              type: 'string',
              required: true,
              description: "Email del dipendente di cui leggere le certificazioni.",
            },
          ]}
          responseModel={USER_CERTS_RESPONSE_MODEL}
          example={
            'GET <BASE_URL>/api/public/users/certifications?email=mario.rossi@esempio.it\nx-api-key: <chiave>'
          }
        />
      </Card>
    </div>
  );
}
